/**
 * Lazy-load the Maps JavaScript API once per page (dynamic library import).
 * Callers should use google.maps.importLibrary(...) for Places / Maps.
 */

declare global {
  interface Window {
    google?: typeof google;
    __wtGoogleMapsPromise?: Promise<typeof google>;
    __wtGoogleMapsInit?: () => void;
  }
}

export function getGoogleMapsApiKey(): string {
  return String(import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '').trim();
}

export function isGoogleMapsJsConfigured(): boolean {
  const key = getGoogleMapsApiKey();
  return key.length > 0 && key.startsWith('AIza');
}

/** Load Maps JS bootstrap; resolves with `google` namespace. */
export function loadGoogleMaps(): Promise<typeof google> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Google Maps is only available in the browser'));
  }
  if (window.google?.maps) {
    return Promise.resolve(window.google);
  }
  if (window.__wtGoogleMapsPromise) {
    return window.__wtGoogleMapsPromise;
  }
  if (!isGoogleMapsJsConfigured()) {
    return Promise.reject(new Error('Google Maps API key is not configured'));
  }

  window.__wtGoogleMapsPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-wt-google-maps]');
    if (existing) {
      const finish = () => {
        if (window.google?.maps) resolve(window.google);
        else reject(new Error('Google Maps failed to load'));
      };
      if (window.google?.maps) {
        finish();
        return;
      }
      window.__wtGoogleMapsInit = finish;
      existing.addEventListener('error', () => {
        window.__wtGoogleMapsPromise = undefined;
        reject(new Error('Google Maps failed to load'));
      });
      return;
    }

    const callbackName = '__wtGoogleMapsInit';
    window[callbackName] = () => {
      delete window[callbackName];
      if (window.google?.maps) resolve(window.google);
      else {
        window.__wtGoogleMapsPromise = undefined;
        reject(new Error('Google Maps failed to load'));
      }
    };

    const script = document.createElement('script');
    script.dataset.wtGoogleMaps = '1';
    script.async = true;
    // loading=async is required by Google's best-practice loader check;
    // callback (not script.onload) signals API readiness when loading=async.
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
      getGoogleMapsApiKey()
    )}&v=weekly&loading=async&callback=${callbackName}`;
    script.onerror = () => {
      delete window[callbackName];
      window.__wtGoogleMapsPromise = undefined;
      reject(new Error('Google Maps failed to load'));
    };
    document.head.appendChild(script);
  });

  return window.__wtGoogleMapsPromise;
}
